/** Clause ordering and numbering for the contract template editor.
 *
 *  Separate from the page because this is the part that can be *wrong* in a way
 *  nobody notices until a contract is in front of a counterparty: a clause
 *  numbered 12 that the agreement refers to as 11, or two clauses both called 8.
 *  It is pure, so it is tested directly.
 *
 *  The sections are stored FLAT with a `level`. A level-1 clause owns every
 *  level-2 subsection that follows it until the next level-1 — that implicit
 *  ownership is what dragging has to preserve.
 */
import type { TemplateSection } from '../types';

export interface Block {
  /** The level-1 clause. */
  parent: TemplateSection;
  /** Its level-2 subsections, in order. */
  children: TemplateSection[];
}

/** A leading clause number in a heading: `8.`, `11.2`, `1.1 `. */
const NUMBER_PREFIX = /^\s*\d+(?:\.\d+)*\.?\s*/;

/** Group the flat list into clauses and their subsections. */
export function toBlocks(sections: TemplateSection[]): Block[] {
  const blocks: Block[] = [];
  for (const section of sections) {
    if ((section.level || 1) === 1 || blocks.length === 0) {
      // A level-2 with no clause before it would otherwise be dropped on the
      // floor. Promoted to its own block so nothing can vanish from the editor.
      blocks.push({ parent: section, children: [] });
    } else {
      blocks[blocks.length - 1].children.push(section);
    }
  }
  return blocks;
}

/** Flatten blocks back to the stored shape, with `order` matching what is shown. */
export function toFlat(blocks: Block[]): TemplateSection[] {
  const out: TemplateSection[] = [];
  for (const block of blocks) {
    out.push(block.parent);
    for (const child of block.children) out.push(child);
  }
  return out.map((s, i) => ({ ...s, order: i }));
}

function applyNumber(heading: string, next: string, isClause: boolean): string {
  const rest = (heading || '').replace(NUMBER_PREFIX, '').trim();
  if (!rest) return next;
  return isClause ? `${next}. ${rest}` : `${next} ${rest}`;
}

/**
 * Renumber in document order.
 *
 * Three rules, each of which exists because the alternative produces a wrong
 * document rather than an untidy one:
 *
 * - Only sections that were ALREADY numbered get numbers. The title block,
 *   `Background`, `Signatures` and the annexures carry no clause number in the
 *   agreement, and inventing one for them would renumber the whole contract.
 * - Excluded sections are skipped in the count, so a contract with a clause
 *   removed reads 1, 2, 3 rather than 1, 2, 4.
 * - Subsections number within their clause and reset at each one.
 */
export function renumber(sections: TemplateSection[]): TemplateSection[] {
  let clause = 0;
  let sub = 0;
  let clauseNumbered = false;

  return sections.map((section) => {
    const level = section.level || 1;
    const wasNumbered = !!(section.number || '').trim();

    if (level === 1) {
      sub = 0;
      if (!wasNumbered || section.excluded) {
        clauseNumbered = false;
        return { ...section, number: '' };
      }
      clause += 1;
      clauseNumbered = true;
      const next = String(clause);
      return { ...section, number: next, heading: applyNumber(section.heading || '', next, true) };
    }

    if (!wasNumbered || section.excluded || !clauseNumbered) {
      return { ...section, number: '' };
    }
    sub += 1;
    const next = `${clause}.${sub}`;
    return { ...section, number: next, heading: applyNumber(section.heading || '', next, false) };
  });
}

export interface NumberChange {
  from: string;
  to: string;
  heading: string;
}

/**
 * Which clause numbers moved.
 *
 * Surfaced to the editor because renumbering does NOT rewrite cross-references
 * inside clause bodies — "as set out in clause 11.3" is prose, and rewriting
 * prose automatically is how a contract quietly starts pointing at the wrong
 * clause. Naming the changes lets a human check them.
 */
export function numberChanges(
  before: TemplateSection[],
  after: TemplateSection[],
): NumberChange[] {
  const was = new Map(before.map((s) => [s.id, (s.number || '').trim()]));
  const out: NumberChange[] = [];
  for (const section of after) {
    const from = was.get(section.id) ?? '';
    const to = (section.number || '').trim();
    if (from && from !== to) {
      out.push({ from, to: to || '(removed)', heading: section.heading || '' });
    }
  }
  return out;
}

/** Bodies that mention any of the numbers that moved. */
export function affectedReferences(
  sections: TemplateSection[],
  changes: NumberChange[],
): string[] {
  if (!changes.length) return [];
  const moved = changes.map((c) => c.from);
  const hits = new Set<string>();
  for (const section of sections) {
    const body = section.body || '';
    for (const number of moved) {
      // Bounded so `1.1` does not match inside `11.10`.
      const pattern = new RegExp(`\\b${number.replace('.', '\\.')}\\b`);
      if (pattern.test(body)) hits.add(section.number || section.heading || section.id);
    }
  }
  return [...hits];
}

/* ── cross-references ────────────────────────────────────────────────────
 *
 * A reference stores the target's ID, never its number. Numbers move every
 * time a clause is dragged or excluded; the identity of the clause being
 * referred to does not. Storing "11.3" would mean every reorder silently
 * repointed half the agreement — which is exactly the failure the reorder
 * warning could only report, not prevent.
 */

export const REF_PATTERN = /\{\{ref:([A-Za-z0-9._:-]+)\}\}/g;

/** How each section should be cited: its clause number, or its heading when it
 *  has no number (`Annexure A`, `Signatures`) — which is how agreements
 *  actually cite those. */
export function refLabel(section: TemplateSection): string {
  return (section.number || '').trim() || (section.heading || '').trim();
}

/** Replace `{{ref:id}}` with what it currently points at. */
export function resolveRefs(text: string, sections: TemplateSection[]): string {
  const byId = new Map(sections.map((s) => [s.id, s]));
  return (text || '').replace(REF_PATTERN, (_match, id: string) => {
    const target = byId.get(id);
    // Both failures are LOUD in the output on purpose. A contract that cites a
    // clause which is not in it is a defect, and a quiet fallback would carry
    // that defect into a document somebody signs.
    if (!target) return '[reference to a deleted clause]';
    if (target.excluded) return '[reference to an excluded clause]';
    return refLabel(target) || '[unnumbered clause]';
  });
}

export interface BrokenRef {
  /** The section containing the broken reference. */
  in: string;
  reason: 'deleted' | 'excluded';
  targetId: string;
}

/** References that would render as a defect, so the editor can say so first. */
export function brokenRefs(sections: TemplateSection[]): BrokenRef[] {
  const byId = new Map(sections.map((s) => [s.id, s]));
  const out: BrokenRef[] = [];
  for (const section of sections) {
    if (section.excluded) continue;      // not in the document; not a problem
    for (const match of (section.body || '').matchAll(REF_PATTERN)) {
      const target = byId.get(match[1]);
      if (!target) {
        out.push({ in: refLabel(section) || section.id, reason: 'deleted', targetId: match[1] });
      } else if (target.excluded) {
        out.push({ in: refLabel(section) || section.id, reason: 'excluded', targetId: match[1] });
      }
    }
  }
  return out;
}
